<template>
  <div class="border">
    <div class="casereport">
      <div class="title-container">
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="xMinYMin"
          id="title-svg"
        >
          <text x="17" y="55" font-size="36">Case Review</text>
        </svg>
      </div>
      <div class="case-dates-container">
        <svg
          viewBox="0 0 100 16.34"
          preserveAspectRatio="xMinYMin"
          id="case-dates-svg"
        >
          <text x="25" y="9" font-size="4" text-anchor="middle">
            Start Time: {{ startTimeString }}
          </text>
          <text x="60" y="9" font-size="4" text-anchor="middle">
            End Time: {{ endTimeString }}
          </text>
          <text x="95" y="9" font-size="4" text-anchor="middle">
            Duration: {{ durationString }}
          </text>
        </svg>
      </div>
      <div class="tables-container">
        <div class="surgerytype-container">
          <div class="demographics-row">
            <svg viewBox="0 0 100 10" preserveAspectRatio="xMinYMid">
              <text x="0" y="5" font-size="5" font-weight="bold">
                Surgery date:
              </text>
              <text x="35" y="5" font-size="5">{{ title }}</text>
            </svg>
          </div>
          <div class="demographics-row">
            <svg viewBox="0 0 100 10" preserveAspectRatio="xMinYMin">
              <text x="0" y="5" font-size="5" font-weight="bold">
                Surgery type:
              </text>
              <text x="35" y="5" font-size="5">{{ surgeryType }}</text>
            </svg>
          </div>
          <div class="demographics-row">
            <svg viewBox="0 0 100 10" preserveAspectRatio="xMinYMin">
              <text x="0" y="5" font-size="5" font-weight="bold">
                Session number:
              </text>
              <text x="43" y="5" font-size="5">ZUHE4-VFLUK-P84F1</text>
            </svg>
          </div>
          <div class="demographics-row">
            <svg viewBox="0 0 100 10" preserveAspectRatio="xMinYMin">
              <text x="0" y="5" font-size="5" font-weight="bold">Age:</text>
              <text x="12" y="5" font-size="5">{{ ageString }}</text>
              <text x="30" y="5" font-size="5" font-weight="bold">Height:</text>
              <text x="49" y="5" font-size="5">{{ heightString }}</text>
              <text x="65" y="5" font-size="5" font-weight="bold">Weight:</text>
              <text x="85" y="5" font-size="5">{{ weightString }}</text>
            </svg>
          </div>
        </div>
        <HypoTable
          id="hypo-table"
          :episodesCount="episodesCount"
          :hypoSeconds="hypoSeconds"
        ></HypoTable>
        <TargetTable
          id="targets-table"
          :totalSeconds="totalSeconds"
          :secondsInTarget="secondsInTarget"
          :secondsUnderTarget="secondsUnderTarget"
          :secondsAboveTarget="secondsAboveTarget"
        ></TargetTable>
        <div class="treatments-container">
          <svg
            class="treatments-svg"
            viewBox="0 0 150 120"
            preserveAspectRatio="xMinYMin"
          >
            <text x="2" y="7" font-size="10" font-weight="bold">Treatment</text>
            <text
              v-for="treatment in markers"
              :key="treatment.id"
              x="8"
              :y="treatment.y"
              text-anchor="end"
              font-weight="bold"
              font-size="8"
            >
              {{ treatment.id }}
            </text>
            <text
              v-for="treatment in markers"
              :key="treatment.id"
              x="12"
              :y="treatment.y"
              text-anchor="start"
              font-size="8"
            >
              {{ treatment.time }}
            </text>
            <image
              v-for="treatment in markers"
              :key="treatment.id"
              x="35"
              :y="treatment.y - 8"
              width="8"
              height="8"
              :href="treatment.imagePath"
            />
            <text
              v-for="treatment in markers"
              :key="treatment.id"
              x="50"
              :y="treatment.y"
              font-size="8"
            >
              {{ treatment.shortName }}
            </text>
          </svg>
          <svg
            class="treatments-svg"
            viewBox="0 0 150 120"
            preserveAspectRatio="xMinYMin"
            v-if="markers.length === 0"
          >
            <text x="35" y="65" text-anchor="start">
              No treatments recorded
            </text>
          </svg>
        </div>
        <div class="exit-button-container">
          <button @mouseup="clickExit">
            <svg viewBox="0 0 181 36">
              <text x="90.5" y="16" font-size="17" text-anchor="middle">
                Close Case Review
              </text>
            </svg>
          </button>
        </div>
      </div>
      <div
        class="plots-container"
        ref="plotsContainer"
        @mousemove="mo"
        @mouseup="captureOff"
      >
        <ReviewPlot
          id="map-plot"
          :xMin="xMin"
          :xMax="xMax"
          :yMin="35"
          :yMax="125"
          :yTicks="mapTickYs"
          title="Mean Arterial Pressure"
          :targetMin="65"
          :targetMax="85"
          :signals="mapSignal"
          :baselineSignals="false"
          :startDate="startTime"
          :endDate="endTime"
          :markers="markers"
        ></ReviewPlot>
        <SimpleTimeAxis
          id="time-axis"
          :startTime="startTime"
          :endTime="endTime"
        ></SimpleTimeAxis>
        <ReviewPlot
          id="trends-plot"
          title="CO / HR / SVR"
          :xMin="xMin"
          :xMax="xMax"
          :yMin="20"
          :yMax="200"
          :yTicks="trendTickYs"
          :signals="trendSignals"
          :baselineSignals="true"
          :baselineIndex="baselineIndex"
          :startDate="startTime"
          :endDate="endTime"
        ></ReviewPlot>
        <div id="baseline-cursor-track" ref="baselineCursorTrack">
          <div
            id="baseline-cursor"
            ref="baselineCursor"
            :style="{ marginLeft: baselineCursorMarginLeft }"
            @mousedown="captureOn"
          >
            <svg
              id="cursor-svg"
              viewBox="0 0 20 8"
              preserveAspectRatio="xMidYMin"
            >
              <rect x="5" y="1" width="10" height="6" fill="#00433b" rx="1" />
              <polygon fill="#00433b" points="0,4.5 4,2 4, 6" />
              <polygon fill="#00433b" points="20,4.5 16,2 16,6" />
              <text font-size="4" x="5" y="5.5" fill="white">
                {{ baselineTime }}
              </text>
              <line
                x1="10"
                y1="0"
                x2="10"
                y2="-144"
                stroke="#FF777C"
                vector-effect="non-scaling-stroke"
                stroke-opacity="0.8"
                stroke-width="1"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HypoTable from '@/components/HypoTable.vue'
import TargetTable from '@/components/TargetTable.vue'
import ReviewPlot from '@/components/ReviewPlot.vue'
import SimpleTimeAxis from '@/components/SimpleTimeAxis.vue'

export default {
  name: 'CaseReport',
  props: {
    surgeryType: {
      type: String,
      default: ''
    },
    sessionNumber: {
      type: String,
      default: ''
    },
    age: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: ''
    },
    weight: {
      type: String,
      default: ''
    },
    mapSignal: {
      type: Array
    },
    trendSignals: {
      type: Array
    },
    startTime: {
      type: Date
    },
    endTime: {
      type: Date
    },
    treatments: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      mapTickYs: [40, 50, 60, 70, 80, 90, 100, 110, 120],
      trendTickYs: [40, 60, 80, 100, 120, 140, 160, 180],
      captureToggle: false,
      baselineCursorMarginLeft: '200px',
      baselineTime: '11:09',
      baselineDateTime: null,
      baselineIndex: 0
    }
  },
  computed: {
    title() {
      if (this.startTime == null) {
        return 'Unknown date'
      }
      return this.startTime.toDateString()
    },
    xMin() {
      if (
        !this.mapSignal ||
        this.mapSignal.length === 0 ||
        !this.mapSignal[0].points ||
        this.mapSignal[0].points.length === 0
      ) {
        return 0
      }
      return this.mapSignal[0].points[0][0]
    },
    xMax() {
      if (
        !this.mapSignal ||
        this.mapSignal.length === 0 ||
        !this.mapSignal[0].points ||
        this.mapSignal[0].points.length === 0
      ) {
        return 100
      }
      return this.mapSignal[0].points[this.mapSignal[0].points.length - 1][0]
    },
    totalSeconds() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return 0
      }
      let count = 0
      this.mapSignal[0].points.forEach(point => {
        if (!isNaN(point[1])) count += 1
      })
      return count
    },
    secondsInTarget() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return 0
      }
      return this.countSecsInRange(this.mapSignal[0].points, 65, 85)
    },
    secondsUnderTarget() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return 0
      }
      return this.countSecsUnderThreshold(this.mapSignal[0].points, 65)
    },
    secondsAboveTarget() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return 0
      }
      return this.countSecsAboveThreshold(this.mapSignal[0].points, 85)
    },
    episodesCount() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return [0, 0, 0]
      }
      return [
        this.countEpisodesUnderThreshold(this.mapSignal[0].points, 75, 60),
        this.countEpisodesUnderThreshold(this.mapSignal[0].points, 65, 60),
        this.countEpisodesUnderThreshold(this.mapSignal[0].points, 55, 60)
      ]
    },
    hypoSeconds() {
      if (!this.mapSignal || !this.mapSignal[0] || !this.mapSignal[0].points) {
        return [0, 0, 0]
      }
      let secsLessThan75 = 0
      let secsLessThan65 = 0
      let secsLessThan55 = 0
      this.mapSignal[0].points.forEach(point => {
        if (point[1] < 75) secsLessThan75 += 1
        if (point[1] < 65) secsLessThan65 += 1
        if (point[1] < 55) secsLessThan55 += 1
      })
      return [secsLessThan75, secsLessThan65, secsLessThan55]
    },
    startTimeString() {
      if (this.startTime == null) return ''
      return (
        String(this.startTime.getHours()).padStart(2, '0') +
        ':' +
        String(this.startTime.getMinutes()).padStart(2, '0')
      )
    },
    endTimeString() {
      if (this.endTime == null) return ''
      return (
        String(this.endTime.getHours()).padStart(2, '0') +
        ':' +
        String(this.endTime.getMinutes()).padStart(2, '0')
      )
    },
    durationString() {
      if (this.endTime == null || this.startTime == null) return ''
      // Dif between times is in ms
      let duration = (this.endTime - this.startTime) / 1000.0
      let hours = Math.floor(duration / 3600).toFixed(0)
      let mins = ((duration % 3600) / 60).toFixed(0)
      if (hours > 0) return `${hours}h ${mins}m`
      else if (mins > 0) return `${mins}m`
      else return '0'
    },
    markers() {
      let i = 1
      return this.treatments.map(treatment => {
        return {
          x: treatment.point[0],
          id: i++,
          shortName: treatment.shortName,
          type: treatment.type,
          y: 10 * i,
          time: treatment.time.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          imagePath: this.imgForType(treatment.type)
        }
      })
    },
    ageString() {
      if (this.age === '') {
        return ''
      } else {
        return this.age + 'yr'
      }
    },
    heightString() {
      if (this.height === '') {
        return ''
      } else {
        return this.height + 'cm'
      }
    },
    weightString() {
      if (this.weight === '') {
        return ''
      } else {
        return this.weight + 'kg'
      }
    }
  },
  methods: {
    getClosestYValInArray(searchTime, array) {
      if (searchTime == null || array == null) return
      // Returns y-value in array that is closest to searchTime
      let idx = 0
      let diff = Math.abs(searchTime - array[0][0])
      for (let i = 1; i < array.length; i++) {
        if (Math.abs(searchTime - array[i][0]) < diff && !isNaN(array[i][1])) {
          diff = Math.abs(searchTime - array[i][0])
          idx = i
        }
      }
      return array[idx][1]
    },
    countEpisodesUnderThreshold(signal, threshold, minDuration) {
      // Returns how many episodes of minDuration (seconds) or longer under threshold
      // have happened in signal
      let count = 0
      let epDurations = []
      let openEp = false
      signal.forEach(point => {
        if (point[1] < threshold) {
          if (!openEp) {
            openEp = true
            epDurations.push(0)
          }
          epDurations[epDurations.length - 1] += 1
        } else openEp = false
      })
      epDurations.forEach(duration => {
        if (duration >= minDuration) count += 1
      })
      return count
    },
    countSecsUnderThreshold(signal, threshold) {
      // Returns seconds under threhsold in signal
      let count = 0
      signal.forEach(point => {
        if (point[1] < threshold) {
          count += 1
        }
      })
      return count
    },
    countSecsAboveThreshold(signal, threshold) {
      // Returns seconds under threhsold in signal
      let count = 0
      signal.forEach(point => {
        if (point[1] > threshold) {
          count += 1
        }
      })
      return count
    },
    countSecsInRange(signal, min, max) {
      // Returns seconds under max, above min, in signal
      let count = 0
      signal.forEach(point => {
        if (point[1] <= max && point[1] >= min) {
          count += 1
        }
      })
      return count
    },
    mo(evt) {
      if (this.captureToggle) {
        let cursorWidth = this.$refs.baselineCursor.getBoundingClientRect()
          .width
        let trackLeft = this.$refs.baselineCursorTrack.getBoundingClientRect()
          .left
        let trackWidth = this.$refs.baselineCursorTrack.getBoundingClientRect()
          .width
        let x = evt.x - trackLeft - cursorWidth / 2.0
        x = Math.min(x, trackWidth - cursorWidth / 2.0)
        x = Math.max(x, -cursorWidth / 2.0)
        this.baselineCursorMarginLeft = x + 'px'
        let timeFraction = (evt.x - trackLeft) / trackWidth
        this.baselineDateTime = new Date(
          this.startTime.getTime() +
            timeFraction * (this.endTime - this.startTime)
        )
        this.baselineTime = this.baselineDateTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        this.baselineIndex = Math.round(
          timeFraction * this.trendSignals[0].points.length
        )
      }
    },
    captureOn() {
      this.captureToggle = true
    },
    captureOff() {
      this.captureToggle = false
    },
    clickExit() {
      this.$emit('exitCaseReview')
    },
    imgForType(type) {
      switch (type) {
        case 'bolus':
          return require('../assets/Treat_item_syringe_icon.svg')
        case 'iv_up':
          return require('../assets/Treat_item_up_arrow.svg')
        case 'iv_down':
          return require('../assets/Treat_item_down_arrow.svg')
        case 'iv_off':
          return require('../assets/Treat_item_off_icon.svg')
        case 'volume':
          return ''
        default:
          return ''
      }
    }
  },
  components: {
    HypoTable,
    TargetTable,
    ReviewPlot,
    SimpleTimeAxis
  }
}
</script>

<style scoped>
.border {
  display: grid;
  background-color: #add4c8;
  padding: 1%;
  box-sizing: border-box;
  grid-template-rows: 100%;
  grid-template-columns: 100%;
}
.casereport {
  background-color: white;
  display: grid;
  grid-template-rows: 15% 85%;
  grid-template-columns: 33.333% 66.666%;
}
.demographics-row {
  height: 100%;
}
.surgerytype-container {
  grid-column-start: 1;
  grid-column-end: 3;
  grid-template-rows: 25% 25% 25% 25%;
  display: grid;
}
.tables-container {
  grid-row: 2;
  grid-column: 1;
  display: grid;
  grid-template-rows: 20% 20% 20% 33% 7%;
  grid-template-columns: 100%;
  box-sizing: border-box;
  padding-left: 5%;
}
.exit-button-container {
  grid-row: 5;
  padding-bottom: 1%;
}
.exit-button-container button {
  border: none;
  padding: 0;
  width: 80%;
  height: 100%;
}
.plots-container {
  grid-row: 2;
  grid-column: 2;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 45% 4% 45% 6%;
  box-sizing: border-box;
  padding-right: 5%;
}
#hypo-table {
  grid-row: 2;
}
#targets-table {
  grid-row: 3;
}
#map-plot {
  grid-row: 1;
  width: 100%;
  height: 100%;
}
#time-axis {
  grid-row: 2;
  width: 100%;
  height: 100%;
}
#trends-plot {
  grid-row: 3;
  width: 100%;
  height: 100%;
}
#baseline-cursor-track {
  margin-left: 6%;
  grid-row: 4;
}
#baseline-cursor {
  user-select: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  width: 10%;
  height: 100%;
  display: grid;
  box-sizing: border-box;
}
#cursor-svg {
  overflow: visible;
  box-sizing: border-box;
  width: 100%;
}
.title-container {
  grid-row: 1;
  grid-column: 1;
  display: flex;
}
#title-svg {
  height: 100%;
  overflow: visible;
}
.case-dates-container {
  grid-row: 1;
  grid-column: 2;
  padding-left: 5%;
  box-sizing: border-box;
  display: flex;
}
#case-dates-svg {
  height: 100%;
  overflow: visible;
}
.treatments-container {
  grid-row: 4;
  display: grid;
}
.treatments-svg {
  height: 100%;
  grid-row: 1;
  grid-column: 1;
  overflow: visible;
}
</style>
